import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Button, Space, Table, Modal, Input, DatePicker, Select, message, Popconfirm, Row, Col, Form } from 'antd';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { API_BASE_URL } from '../../utils/config';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, DeleteOutlined, CheckOutlined, PrinterOutlined, CalendarOutlined } from '@ant-design/icons';
import { trUpper } from '../../utils/trText';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const Gorev = () => {
    const { darkMode } = useTheme();
    const { user } = useAuth();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();
    const [reportDateRange, setReportDateRange] = useState(null);
    const [planDateRange, setPlanDateRange] = useState(null);
    const [reportModalVisible, setReportModalVisible] = useState(false);
    const [planModalVisible, setPlanModalVisible] = useState(false);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const containerStyle = {
        padding: 24,
        ...(darkMode && { backgroundColor: 'var(--dark-bg)', color: 'var(--dark-text)' }),
    };
    const cardStyle = {
        background: darkMode ? 'var(--dark-bg-secondary)' : 'var(--bg-secondary, #fff)',
        border: '1px solid ' + (darkMode ? 'var(--dark-border)' : 'var(--border, #d9d9d9)'),
    };
    const tableWrapperStyle = { marginTop: 16 };
    const modalStyles = darkMode ? {
        content: { backgroundColor: 'var(--dark-bg-secondary)' },
        header: { backgroundColor: 'var(--dark-bg-header)', borderBottom: '1px solid var(--dark-border)' },
        body: { backgroundColor: 'var(--dark-bg-secondary)', color: 'var(--dark-text)' },
        footer: { backgroundColor: 'var(--dark-bg-secondary)', borderTopColor: 'var(--dark-border)' },
    } : undefined;
    const errorMessageStyle = {
        marginBottom: 16,
        padding: 12,
        background: darkMode ? 'rgba(255, 77, 79, 0.1)' : '#fff2f0',
        border: '1px solid ' + (darkMode ? 'var(--dark-danger)' : '#ffccc7'),
        borderRadius: 4,
        color: darkMode ? 'var(--dark-danger)' : undefined,
    };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const auth_token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/gorev/list`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth_token}`
                }
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const result = await response.json();
            const allData = result.tasks || [];    
            setData(allData);
            setPagination({
                current: 1,
                pageSize: pagination.pageSize,
            });
        } catch (err) {
            console.error('Veri yükleme hatası:', err);
            setError(err.message);
            message.error('Görevler yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({
            eklenmeTarihi: dayjs(),
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            metin: record.metin,
            eklenmeTarihi: record.eklenmeTarihi ? dayjs(record.eklenmeTarihi) : dayjs(),
            tamamlanmaTarihi: record.tamamlanmaTarihi ? dayjs(record.tamamlanmaTarihi) : null,
            done: record.done || 0,
        });
        setModalVisible(true);
    };

    const handleDelete = async (record) => {
        try {
            const auth_token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/form/submit-form`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth_token}`,
                },
                body: JSON.stringify({
                    table_name: 'todo_list',
                    function: 'Update',
                    form_data: {
                        id: record.id,
                        active: 0
                    },
                    unique_field: 'id',
                    unique_value: record.id,
                    user_id: user?.id || user?.kullanici_no
                })
            });

            if (!response.ok) {
                throw new Error('Silme işlemi başarısız');
            }
            message.success('Görev silindi');
            loadData();
        } catch (err) {
            console.error('Silme hatası:', err);
            message.error('Görev silinirken hata oluştu');
        }
    };

    const handleComplete = async (record) => {
        try {
            const auth_token = localStorage.getItem('auth_token');
            const response = await fetch(`${API_BASE_URL}/form/submit-form`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth_token}`,
                },
                body: JSON.stringify({
                    table_name: 'todo_list',
                    function: 'Update',
                    form_data: {
                        id: record.id,
                        done: 1,
                        tamamlanmaTarihi: dayjs().format('YYYY-MM-DD')
                    },
                    unique_field: 'id',
                    unique_value: record.id,
                    user_id: user?.id || user?.kullanici_no
                })
            });

            if (!response.ok) {
                throw new Error('Tamamlama işlemi başarısız');
            }
            message.success('Görev tamamlandı');
            loadData();
        } catch (err) {
            console.error('Tamamlama hatası:', err);
            message.error('Görev tamamlanırken hata oluştu');
        }
    };

    const handleSubmit = async (values) => {
        try {
            const auth_token = localStorage.getItem('auth_token');
            const formData = {
                metin: values.metin,
                eklenmeTarihi: values.eklenmeTarihi ? values.eklenmeTarihi.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
                tamamlanmaTarihi: values.tamamlanmaTarihi ? values.tamamlanmaTarihi.format('YYYY-MM-DD') : null,
                done: values.done || 0,
                visible: 1,
                active: 1,
                ekleyen: user?.id || user?.kullanici_no || 0,
            };

            if (editingRecord) {
                formData.id = editingRecord.id;
            }

            const response = await fetch(`${API_BASE_URL}/form/submit-form`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${auth_token}`,
                },
                body: JSON.stringify({
                    table_name: 'todo_list',
                    function: editingRecord ? 'Update' : 'Insert',
                    form_data: formData,
                    unique_field: editingRecord ? 'id' : null,
                    unique_value: editingRecord ? editingRecord.id : null,
                    user_id: user?.id || user?.kullanici_no
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || 'Kayıt işlemi başarısız');
            }
            message.success(editingRecord ? 'Görev güncellendi' : 'Görev eklendi');
            setModalVisible(false);
            form.resetFields();
            loadData();
        } catch (err) {
            console.error('Kayıt hatası:', err);
            message.error(err.message || 'Görev kaydedilirken hata oluştu');
        }
    };

    const getWeeklyReportData = () => {
        if (!reportDateRange || !reportDateRange[0] || !reportDateRange[1]) {
            message.warning('Lütfen tarih aralığı seçin');
            return [];
        }

        const startDate = reportDateRange[0].startOf('day');
        const endDate = reportDateRange[1].endOf('day');

        return data.filter(item => {
            if (!item.tamamlanmaTarihi) return false;
            const tamamlanmaTarihi = dayjs(item.tamamlanmaTarihi);
            return tamamlanmaTarihi.isBetween(startDate, endDate, 'day', '[]') && item.done === 1;
        });
    };

    const getWeeklyPlanData = () => {
        if (!planDateRange || !planDateRange[0] || !planDateRange[1]) {
            message.warning('Lütfen tarih aralığı seçin');
            return [];
        }

        const startDate = planDateRange[0].startOf('day');
        const endDate = planDateRange[1].endOf('day');

        return data.filter(item => {
            if (!item.eklenmeTarihi) return false;
            const eklenmeTarih = dayjs(item.eklenmeTarihi);
            return eklenmeTarih.isBetween(startDate, endDate, 'day', '[]') && (item.done === 0 || item.done === '0' || !item.done);
        });
    };

    const generateWeeklyReport = () => {
        if (!reportDateRange || !reportDateRange[0] || !reportDateRange[1]) {
            message.warning('Lütfen tarih aralığı seçin');
            return;
        }

        const reportData = getWeeklyReportData();
        if (reportData.length === 0) {
            message.warning('Seçilen tarih aralığında tamamlanmış görev bulunamadı');
            return;
        }

        const userName = user?.ad_soyad || user?.name || 'Kullanıcı';
        const reportDate = dayjs().format('DD.MM.YYYY');
        const startDate = reportDateRange[0].format('DD.MM.YYYY');
        const endDate = reportDateRange[1].format('DD.MM.YYYY');

        const printWindow = window.open('', '_blank');
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Haftalık Rapor</title>
                <style>
                    @page {
                        margin: 20mm;
                        size: A4;
                    }
                    body {
                        font-family: 'Segoe UI', Arial, sans-serif;
                        margin: 0;
                        padding: 0;
                        background: white;
                        color: #000;
                    }
                    .header {
                        display: flex;
                        align-items: center;
                        margin-bottom: 20px;
                    }
                    .header-icon {
                        width: 40px;
                        height: 40px;
                        margin-right: 15px;
                        background: #1890ff;
                        border-radius: 4px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 24px;
                    }
                    .header-title {
                        font-size: 28px;
                        font-weight: bold;
                        color: #1890ff;
                    }
                    .info-section {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        padding: 15px;
                        background: #f5f5f5;
                        border-radius: 4px;
                    }
                    .info-left, .info-right {
                        flex: 1;
                    }
                    .info-label {
                        font-weight: bold;
                        margin-bottom: 5px;
                        color: #333;
                    }
                    .info-value {
                        color: #666;
                    }
                    .divider {
                        height: 2px;
                        background: #52c41a;
                        margin: 20px 0;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th {
                        background: #fafafa;
                        padding: 12px;
                        text-align: left;
                        border-bottom: 2px solid #52c41a;
                        font-weight: bold;
                        color: #333;
                    }
                    td {
                        padding: 10px 12px;
                        border-bottom: 1px solid #e8e8e8;
                    }
                    tr:hover {
                        background: #f5f5f5;
                    }
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="header-title">HAFTALIK RAPOR</div>
                </div>
                <div class="info-section">
                    <div class="info-left">
                        <div class="info-label">ADI SOYADI</div>
                        <div class="info-value">${trUpper(userName)}</div>
                        <div style="margin-top: 15px;">
                            <div class="info-label">TARİH</div>
                            <div class="info-value">${reportDate}</div>
                        </div>
                    </div>
                    <div class="info-right">
                        <div class="info-label">BİLGİ İŞLEM</div>
                        <div style="margin-top: 15px;">
                            <div class="info-label">GEÇERLİLİK</div>
                            <div class="info-value">${startDate} - ${endDate}</div>
                        </div>
                    </div>
                </div>
                <div class="divider"></div>
                <table>
                    <thead>
                        <tr>
                            <th>İŞ TANIMI</th>
                            <th>EKLENME TARİHİ</th>
                            <th>TAMAMLANMA TARİHİ</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${reportData.map(item => {
                            const eklenmeTarih = item.eklenmeTarihi ? dayjs(item.eklenmeTarihi).format('DD.MM.YYYY') : '-';
                            const tamamlanmaTarihi = item.tamamlanmaTarihi ? dayjs(item.tamamlanmaTarihi).format('DD.MM.YYYY') : '-';
                            return `
                                <tr>
                                    <td>${item.metin || '-'}</td>
                                    <td>${eklenmeTarih}</td>
                                    <td>${tamamlanmaTarihi}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const generateWeeklyPlan = () => {
        if (!planDateRange || !planDateRange[0] || !planDateRange[1]) {
            message.warning('Lütfen tarih aralığı seçin');
            return;
        }

        const planData = getWeeklyPlanData();
        if (planData.length === 0) {
            message.warning('Seçilen tarih aralığında yapılacak görev bulunamadı');
            return;
        }

        const userName = user?.ad_soyad || user?.name || 'Kullanıcı';
        const planDate = dayjs().format('DD.MM.YYYY');
        const startDate = planDateRange[0].format('DD.MM.YYYY');
        const endDate = planDateRange[1].format('DD.MM.YYYY');

        const printWindow = window.open('', '_blank');
        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>Haftalık İş Planı</title>
                <style>
                    @page {
                        margin: 20mm;
                        size: A4;
                    }
                    body {
                        font-family: 'Segoe UI', Arial, sans-serif;
                        margin: 0;
                        padding: 0;
                        background: white;
                        color: #000;
                    }
                    .header {
                        display: flex;
                        align-items: center;
                        margin-bottom: 20px;
                    }
                    .header-icon {
                        width: 40px;
                        height: 40px;
                        margin-right: 15px;
                        background: #1890ff;
                        border-radius: 4px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: white;
                        font-size: 24px;
                    }
                    .header-title {
                        font-size: 28px;
                        font-weight: bold;
                        color: #1890ff;
                    }
                    .info-section {
                        display: flex;
                        justify-content: space-between;
                        margin-bottom: 20px;
                        padding: 15px;
                        background: #f5f5f5;
                        border-radius: 4px;
                    }
                    .info-left, .info-right {
                        flex: 1;
                    }
                    .info-label {
                        font-weight: bold;
                        margin-bottom: 5px;
                        color: #333;
                    }
                    .info-value {
                        color: #666;
                        background: #e8e8e8;
                        padding: 5px 10px;
                        border-radius: 4px;
                        display: inline-block;
                        min-width: 150px;
                    }
                    .divider {
                        height: 2px;
                        background: #1890ff;
                        margin: 20px 0;
                    }
                    table {
                        width: 100%;
                        border-collapse: collapse;
                        margin-top: 10px;
                    }
                    th {
                        background: #fafafa;
                        padding: 12px;
                        text-align: left;
                        border-bottom: 2px solid #1890ff;
                        font-weight: bold;
                        color: #333;
                    }
                    td {
                        padding: 10px 12px;
                        border-bottom: 1px solid #e8e8e8;
                    }
                    tr:hover {
                        background: #f5f5f5;
                    }
                    @media print {
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .no-print {
                            display: none;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="header">
                    <div class="header-title">HAFTALIK İŞ PLANI</div>
                </div>
                <div class="info-section">
                    <div class="info-left">
                        <div class="info-label">ADI SOYADI</div>
                        <div class="info-value">${trUpper(userName)}</div>
                        <div style="margin-top: 15px;">
                            <div class="info-label">TARİH</div>
                            <div class="info-value">${planDate}</div>
                        </div>
                    </div>
                    <div class="info-right">
                        <div class="info-label">BİRİM</div>
                        <div class="info-value">BİLGİ İŞLEM</div>
                        <div style="margin-top: 15px;">
                            <div class="info-label">GEÇERLİLİK</div>
                            <div class="info-value">${startDate} - ${endDate}</div>
                        </div>
                    </div>
                </div>
                <div class="divider"></div>
                <table>
                    <thead>
                        <tr>
                            <th>İŞ TANIMI</th>
                            <th>EKLENME TARİHİ</th>
                            <th>HEDEF TARİH</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${planData.map(item => {
                            const eklenmeTarih = item.eklenmeTarihi ? dayjs(item.eklenmeTarihi).format('DD.MM.YYYY') : '-';
                            const hedefTarih = item.tamamlanmaTarihi ? dayjs(item.tamamlanmaTarihi).format('DD.MM.YYYY') : eklenmeTarih;
                            return `
                                <tr>
                                    <td>${item.metin || '-'}</td>
                                    <td>${eklenmeTarih}</td>
                                    <td>${hedefTarih}</td>
                                </tr>
                            `;
                        }).join('')}
                    </tbody>
                </table>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
            printWindow.print();
        }, 250);
    };

    const columns = [
        {
            title: 'İş Tanımı',
            dataIndex: 'metin',
            key: 'metin',
            ellipsis: true,
        },
        {
            title: 'Eklenme Tarihi',
            dataIndex: 'eklenmeTarihi',
            key: 'eklenmeTarihi',
            width: 150,
            render: (text) => text ? dayjs(text).format('DD.MM.YYYY') : '-',
        },
        {
            title: 'Tamamlanma Tarihi',
            dataIndex: 'tamamlanmaTarihi',
            key: 'tamamlanmaTarihi',
            width: 150,
            render: (text) => text ? dayjs(text).format('DD.MM.YYYY') : '-',
        },
        {
            title: 'Durum',
            dataIndex: 'done',
            key: 'done',
            width: 100,
            render: (done) => (
                <span style={{ color: done === 1 || done === '1' ? '#52c41a' : '#faad14' }}>
                    {done === 1 || done === '1' ? 'Tamamlandı' : 'Beklemede'}
                </span>
            ),
        },
        {
            title: 'İşlemler',
            key: 'actions',
            width: 200,
            fixed: 'right',
            render: (_, record) => (
                <Space size="small">
                    {record.done !== 1 && record.done !== '1' && (
                        <Button
                            type="primary"
                            size="small"
                            icon={<CheckOutlined />}
                            onClick={() => handleComplete(record)}
                            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                        >
                            Tamamla
                        </Button>
                    )}
                    <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Düzenle
                    </Button>
                    <Popconfirm
                        title="Bu görevi silmek istediğinize emin misiniz?"
                        onConfirm={() => handleDelete(record)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button
                            className={darkMode ? "btn-edit-dark" : "btn-edit-light"}  
                            type="default"
                            size="small"
                            icon={<DeleteOutlined />}
                        >
                            Sil
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const darkThemeStyles = `
        body[data-theme='dark'] .gorev-table .ant-table { background-color: var(--dark-bg-secondary) !important; color: var(--dark-text) !important; }
        body[data-theme='dark'] .gorev-table .ant-table-thead > tr > th { background-color: var(--dark-bg-header) !important; color: var(--dark-text) !important; border-bottom: 1px solid var(--dark-border) !important; font-weight: 600 !important; }
        body[data-theme='dark'] .gorev-table .ant-table-tbody > tr > td { background-color: var(--dark-bg-secondary) !important; border-bottom: 1px solid var(--dark-border) !important; color: var(--dark-text) !important; }
        body[data-theme='dark'] .gorev-table .ant-table-tbody > tr:hover > td { background-color: var(--dark-bg-header) !important; }
        body[data-theme='dark'] .gorev-table .ant-pagination-item { background-color: var(--dark-bg-secondary) !important; border-color: var(--dark-border) !important; }
        body[data-theme='dark'] .gorev-table .ant-pagination-item a { color: var(--dark-text) !important; }
        body[data-theme='dark'] .gorev-table .ant-pagination-item-active { background-color: var(--color-primary) !important; border-color: var(--color-primary) !important; }
        body[data-theme='dark'] .gorev-table .ant-pagination-item-active a { color: #fff !important; }
        body[data-theme='dark'] .gorev-table .ant-pagination-prev, body[data-theme='dark'] .gorev-table .ant-pagination-next { background-color: var(--dark-bg-secondary) !important; border-color: var(--dark-border) !important; color: var(--dark-text) !important; }
    `;

    return (
        <>
        <style dangerouslySetInnerHTML={{ __html: darkThemeStyles }} />
        <div style={containerStyle}>
            <Card style={cardStyle}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Col>
                        <Title level={3} style={{ color: darkMode ? 'var(--dark-text)' : '#000', margin: 0 }}>
                            Görev Yönetimi
                        </Title>
                    </Col>
                    <Col>
                        <Space>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={handleAdd}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-primary)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-primary)' : undefined,
                                }}
                            >
                                Yeni Görev
                            </Button>
                            <Button
                                icon={<CalendarOutlined />}
                                onClick={() => setReportModalVisible(true)}
                            >
                                Haftalık Rapor
                            </Button>
                            <Button
                                icon={<CalendarOutlined />}
                                onClick={() => setPlanModalVisible(true)}
                            >
                                Haftalık İş Planı
                            </Button>
                        </Space>
                    </Col>
                </Row>

                {error && (
                    <div style={errorMessageStyle}>
                        <Text type="danger" style={{ color: darkMode ? 'var(--dark-danger)' : undefined }}>{error}</Text>
                    </div>
                )}

                <div style={tableWrapperStyle} className={darkMode ? 'gorev-table' : ''}>
                <Table
                    columns={columns}
                    dataSource={data}
                    loading={loading}
                    rowKey="id"
                    className={darkMode ? 'dark-table' : ''}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: data.length,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} görev`,
                        pageSizeOptions: ['10', '25', '50', '100'],
                        onChange: (page, pageSize) => {
                            setPagination({
                                current: page,
                                pageSize: pageSize,
                            });
                        },
                        onShowSizeChange: (current, size) => {
                            setPagination({
                                current: 1,
                                pageSize: size,
                            });
                        },
                    }}
                    scroll={{ x: 'max-content' }}
                    style={{
                        backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                    }}
                />
                </div>
            </Card>

            <Modal
                title={editingRecord ? 'Görevi Düzenle' : 'Yeni Görev Ekle'}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                okText="Kaydet"
                cancelText="İptal"
                width={600}
                styles={modalStyles}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        label="İş Tanımı"
                        name="metin"
                        rules={[{ required: true, message: 'Lütfen iş tanımı girin' }]}
                    >
                        <Input.TextArea rows={4} placeholder="Görev açıklamasını girin" />
                    </Form.Item>
                    <Form.Item
                        label="Eklenme Tarihi"
                        name="eklenmeTarihi"
                        rules={[{ required: true, message: 'Lütfen eklenme tarihi seçin' }]}
                    >
                        <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                    </Form.Item>
                    <Form.Item
                        label="Hedef/Tamamlanma Tarihi"
                        name="tamamlanmaTarihi"
                    >
                        <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
                    </Form.Item>
                    <Form.Item
                        label="Durum"
                        name="done"
                    >
                        <Select>
                            <Option value={0}>Beklemede</Option>
                            <Option value={1}>Tamamlandı</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>

            <Modal
                title="Haftalık Rapor"
                open={reportModalVisible}
                onCancel={() => {
                    setReportModalVisible(false);
                    setReportDateRange(null);
                }}
                styles={modalStyles}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setReportModalVisible(false);
                        setReportDateRange(null);
                    }}>
                        İptal
                    </Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={generateWeeklyReport}>
                        Yazdır
                    </Button>,
                ]}
                width={500}
            >
                <Form layout="vertical">
                    <Form.Item label="Tarih Aralığı" required>
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                            value={reportDateRange}
                            onChange={(dates) => setReportDateRange(dates)}
                        />
                    </Form.Item>
                    <Text type="secondary">
                        Seçilen tarih aralığında tamamlanmış görevler raporlanacaktır.
                    </Text>
                </Form>
            </Modal>

            <Modal
                title="Haftalık İş Planı"
                open={planModalVisible}
                onCancel={() => {
                    setPlanModalVisible(false);
                    setPlanDateRange(null);
                }}
                styles={modalStyles}
                footer={[
                    <Button key="cancel" onClick={() => {
                        setPlanModalVisible(false);
                        setPlanDateRange(null);
                    }}>
                        İptal
                    </Button>,
                    <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={generateWeeklyPlan}>
                        Yazdır
                    </Button>,
                ]}
                width={500}
            >
                <Form layout="vertical">
                    <Form.Item label="Tarih Aralığı" required>
                        <RangePicker
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                            value={planDateRange}
                            onChange={setPlanDateRange}
                        />
                    </Form.Item>
                    <Text type="secondary">
                        Seçilen tarih aralığında eklenen ve henüz tamamlanmamış görevler planlanacaktır.
                    </Text>
                </Form>
            </Modal>
        </div>
        </>
    );
};

export default Gorev;
